import { db } from '../firebase-config';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';

/**
 * Convert email to document ID format (must match emailToDocId in simplifiedAuthService)
 * Example: abbas.merchant@drishti.works -> abbas_merchant_at_drishti_works
 */
const emailToDocId = (email) => {
  if (!email) return null;
  return email.replace(/[.@]/g, (match) => {
    if (match === '.') return '_';
    if (match === '@') return '_at_';
    return match;
  });
};

/**
 * Check if the current user has admin access in any organization
 * @param {string} userUid - The user's UID
 * @param {string} userEmail - The user's email (optional, for better member lookup)
 * @returns {Promise<{isAdmin: boolean, organizations: Array}>}
 */
export const checkAdminAccess = async (userUid, userEmail = null) => {
  try {
    if (!userUid) {
      console.log('checkAdminAccess: No userUid provided');
      return { isAdmin: false, organizations: [] };
    }

    console.log('checkAdminAccess: Checking admin access for user:', userUid, 'email:', userEmail);

    // Get all organizations
    let orgsSnapshot;
    try {
    const organizationsRef = collection(db, 'organizations');
      orgsSnapshot = await getDocs(organizationsRef);
    console.log('checkAdminAccess: Found organizations:', orgsSnapshot.docs.length);
    } catch (queryError) {
      console.error('❌ Error querying organizations collection:', queryError);
      console.log('checkAdminAccess: Falling back to direct organization check...');
      orgsSnapshot = { docs: [] }; // Empty snapshot to trigger fallback
    }
    
    const adminOrganizations = [];
    
    // If no organizations found via query, check the known organization directly
    if (orgsSnapshot.docs.length === 0) {
      console.log('checkAdminAccess: No organizations found in collection query, checking known organization directly...');
      const knownOrgId = 'drishti_lifesaving_org_id';
      
      // Try to get organization document (may not exist, but member subcollection might still be accessible)
      const orgDocRef = doc(db, 'organizations', knownOrgId);
      const orgDocSnap = await getDoc(orgDocRef);
      const orgData = orgDocSnap.exists() ? orgDocSnap.data() : null;
      const orgName = orgData?.name || 'Drishti Lifesaving';
      
      if (orgDocSnap.exists()) {
        console.log('checkAdminAccess: Known organization exists:', knownOrgId);
      } else {
        console.log('checkAdminAccess: Organization document not found, but checking members subcollection anyway...');
      }
      
      // PRIORITY 1: Try email-based document ID first (since that's how documents are stored)
      let memberDoc = null;
      if (userEmail) {
        const memberId = emailToDocId(userEmail);
        console.log('checkAdminAccess: Looking for member with email-normalized ID:', memberId);
        memberDoc = await getDoc(doc(db, 'organizations', knownOrgId, 'members', memberId));
        const emailLookupExists = memberDoc.exists();
        const emailLookupRole = memberDoc.exists() ? memberDoc.data()?.role : null;
        console.log('checkAdminAccess: Email-based lookup result:', emailLookupExists, emailLookupExists ? `Role: ${emailLookupRole}` : '');
      }
      
      // PRIORITY 2: Fallback to UID-based lookup (for backward compatibility)
      if (!memberDoc || !memberDoc.exists()) {
        console.log('checkAdminAccess: Fallback to UID-based lookup:', userUid);
        memberDoc = await getDoc(doc(db, 'organizations', knownOrgId, 'members', userUid));
        const uidLookupExists = memberDoc.exists();
        const uidLookupRole = memberDoc.exists() ? memberDoc.data()?.role : null;
        console.log('checkAdminAccess: UID-based lookup result:', uidLookupExists, uidLookupExists ? `Role: ${uidLookupRole}` : '');
      }
      
      if (memberDoc && memberDoc.exists()) {
        const memberData = memberDoc.data();
        console.log('checkAdminAccess: User is member with role:', memberData.role, 'for organization:', orgName);
        
        // Case-insensitive role check for safety
        if (memberData.role && memberData.role.toLowerCase() === 'admin') {
          adminOrganizations.push({
            id: knownOrgId,
            name: orgName,
            role: memberData.role
          });
          console.log('checkAdminAccess: ✅ User is admin in organization:', orgName);
        } else {
          console.log('checkAdminAccess: User is member but role is not admin:', memberData.role);
        }
      } else {
        console.log('checkAdminAccess: User is not a member of the known organization');
      }
    } else {
      // Original logic: iterate through found organizations
      for (const orgDoc of orgsSnapshot.docs) {
        const orgData = orgDoc.data();
        console.log('checkAdminAccess: Checking organization:', orgDoc.id, orgData.name);
        
        // PRIORITY 1: Try email-based document ID first (since that's how documents are stored)
        let memberDoc = null;
        if (userEmail) {
          const memberId = emailToDocId(userEmail);
          console.log('checkAdminAccess: Looking for member with email-normalized ID:', memberId);
          memberDoc = await getDoc(doc(db, 'organizations', orgDoc.id, 'members', memberId));
          const emailLookupExists = memberDoc.exists();
          const emailLookupRole = memberDoc.exists() ? memberDoc.data()?.role : null;
          console.log('checkAdminAccess: Email-based lookup result:', emailLookupExists, emailLookupExists ? `Role: ${emailLookupRole}` : '');
        }
        
        // PRIORITY 2: Fallback to UID-based lookup (for backward compatibility)
        if (!memberDoc || !memberDoc.exists()) {
          console.log('checkAdminAccess: Fallback to UID-based lookup:', userUid);
          memberDoc = await getDoc(doc(db, 'organizations', orgDoc.id, 'members', userUid));
          const uidLookupExists = memberDoc.exists();
          const uidLookupRole = memberDoc.exists() ? memberDoc.data()?.role : null;
          console.log('checkAdminAccess: UID-based lookup result:', uidLookupExists, uidLookupExists ? `Role: ${uidLookupRole}` : '');
        }
      
        if (memberDoc && memberDoc.exists()) {
          const memberData = memberDoc.data();
          console.log('checkAdminAccess: User is member with role:', memberData.role, 'for organization:', orgData.name);
          
          // Case-insensitive role check for safety
          if (memberData.role && memberData.role.toLowerCase() === 'admin') {
            adminOrganizations.push({
              id: orgDoc.id,
              name: orgData.name,
              role: memberData.role
            });
            console.log('checkAdminAccess: ✅ User is admin in organization:', orgData.name);
          } else {
            console.log('checkAdminAccess: User is member but role is not admin:', memberData.role);
        }
      } else {
        console.log('checkAdminAccess: User is not a member of organization:', orgData.name);
        }
      }
    }
    
    console.log('checkAdminAccess: Final result - isAdmin:', adminOrganizations.length > 0, 'organizations:', adminOrganizations);
    
    return {
      isAdmin: adminOrganizations.length > 0,
      organizations: adminOrganizations
    };
  } catch (error) {
    console.error('❌ Error checking admin access:', error);
    console.error('❌ Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name,
      userUid: userUid,
      userEmail: userEmail
    });
    return { isAdmin: false, organizations: [] };
  }
};

/**
 * Check if user has specific role in an organization
 * @param {string} userUid - The user's UID
 * @param {string} organizationId - The organization ID
 * @param {string} requiredRole - The required role (optional)
 * @param {string} userEmail - The user's email (optional, for better member lookup)
 * @returns {Promise<boolean>}
 */
export const checkUserRole = async (userUid, organizationId, requiredRole = null, userEmail = null) => {
  try {
    if (!userUid || !organizationId) {
      return false;
    }

    // PRIORITY 1: Try email-based document ID first (since that's how documents are stored)
    let memberDoc = null;
    if (userEmail) {
      const memberId = emailToDocId(userEmail);
      memberDoc = await getDoc(doc(db, 'organizations', organizationId, 'members', memberId));
    }
    
    // PRIORITY 2: Fallback to UID-based lookup (for backward compatibility)
    if (!memberDoc || !memberDoc.exists()) {
      memberDoc = await getDoc(doc(db, 'organizations', organizationId, 'members', userUid));
    }
    
    if (!memberDoc.exists()) {
      return false;
    }
    
    const memberData = memberDoc.data();
    
    if (requiredRole) {
      // Case-insensitive role check
      return memberData.role && memberData.role.toLowerCase() === requiredRole.toLowerCase();
    }
    
    return true; // User is a member of the organization
  } catch (error) {
    console.error('Error checking user role:', error);
    return false;
  }
};

/**
 * Get user's permissions based on their role
 * @param {string} userUid - The user's UID
 * @param {string} organizationId - The organization ID
 * @param {string} userEmail - The user's email (optional, for better member lookup)
 * @returns {Promise<Object>}
 */
export const getUserPermissions = async (userUid, organizationId, userEmail = null) => {
  try {
    if (!userUid || !organizationId) {
      return {};
    }

    // PRIORITY 1: Try email-based document ID first (since that's how documents are stored)
    let memberDoc = null;
    if (userEmail) {
      const memberId = emailToDocId(userEmail);
      memberDoc = await getDoc(doc(db, 'organizations', organizationId, 'members', memberId));
    }
    
    // PRIORITY 2: Fallback to UID-based lookup (for backward compatibility)
    if (!memberDoc || !memberDoc.exists()) {
      memberDoc = await getDoc(doc(db, 'organizations', organizationId, 'members', userUid));
    }
    
    if (!memberDoc.exists()) {
      return {};
    }
    
    const memberData = memberDoc.data();
    const userRole = memberData.role;
    
    // Get role permissions
    const roleDoc = await getDoc(doc(db, 'roles', userRole));
    
    if (!roleDoc.exists()) {
      return {};
    }
    
    const roleData = roleDoc.data();
    return roleData.permissions || {};
  } catch (error) {
    console.error('Error getting user permissions:', error);
    return {};
  }
};

/**
 * Check if user has permission for a specific action
 * @param {string} userUid - The user's UID
 * @param {string} organizationId - The organization ID
 * @param {string} permission - The permission to check
 * @returns {Promise<boolean>}
 */
export const hasPermission = async (userUid, organizationId, permission) => {
  try {
    const permissions = await getUserPermissions(userUid, organizationId);
    
    if (permissions[permission] === true) {
      return true;
    }
    
    if (Array.isArray(permissions[permission]) && permissions[permission].length > 0) {
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
};
